"use client";

import { useEffect, useId, useState, type ReactNode } from "react";
import { Badge } from "./badge";
import { DropdownMenue, MENUE_EINTRAG, MENUE_EINTRAG_GEFAHR } from "./dropdown-menue";
import { initialen } from "./formular/auswahl-konfiguration";

/**
 * Punkt-Zeile (Design-Guide `punkt-zeile.md`, Besprechung-Seite neu, Betreiber 19.10.2026 „Agenda A“): EINE Zeilenform für einen Punkt der Agenda in
 * Vorbereiten, Durchführen und Nachbereiten. Feste Leselinie bei jeder Zeile von gleicher Höhe (mindestens 56 px): links Nummer und Titel, rechts die
 * Kennzeichen des Punkts (nur, was es gibt: wichtig n, Bezüge n, Kommentare n, Initialen der vortragenden Person; 0 ist nie ein Kennzeichen), danach das
 * Zeilenmenü (⋮, Steuerung) und der Pfeil. Die GANZE Zeile klappt auf (kein „Mehr“-Link): der Inhalt steht erst nach dem Aufklappen im Bild und wird erst
 * dann gerendert; leere Zustände („Noch keine Bezüge“) gehören in den Aufklappbereich, nie in die Zeile. Neue Art von Inhalt = eine Gruppe im Aufklappbereich
 * plus ein Kennzeichen im Cluster (höchstens vier Plätze, der Rest als „+n“).
 *
 * Der Baustein prüft keine Rechte und kennt keine Daten: der Aufrufer reicht Kennzeichen, Menü und Inhalt (die Menüeinträge nur, wenn die jeweilige Action
 * erlaubt ist, Anbieten ≤ Erlauben, r109/r110). „Aktueller Punkt“ (`aktuell`) ist eine Marke am Punkt, nie eine Aussage über Personen (r177).
 *
 * Abweichung AW-121: Eigenbau aus Preline-Bausteinen (Zeilenform `icon-action-list-with-dashed-dividers`, Aufklappen `accordion-always-open`); der Katalog
 * hat keinen Block „Zeile mit Kennzeichen-Cluster und Aufklappbereich“.
 */

/** Zeilenmenü-Auslöser „⋮“ (Kette wie die Zeilenmenüs der Tabellen; `knopf-ausnahmen.json`: der Baustein DropdownMenue setzt das Element selbst). */
const PUNKT_MENUE_KNOPF =
  "relative z-10 size-8 inline-flex justify-center items-center rounded-md text-muted-foreground-1 hover:bg-muted-hover focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus disabled:opacity-50 disabled:pointer-events-none";

const KOPF_KNOPF =
  "grow min-w-0 py-3 flex items-center gap-x-3 text-start rounded-lg focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus";
const NUMMER =
  "shrink-0 inline-flex size-8 items-center justify-center rounded-lg border border-layer-line bg-layer text-sm font-medium tabular-nums text-foreground";
const KREIS = "inline-flex shrink-0 items-center justify-center size-6 rounded-full bg-surface text-xs font-medium text-surface-foreground";
/** Höchstens vier Plätze im Cluster (Kennzeichen und Initialen); mehr = die ersten drei und „+n“. */
export const PUNKT_CLUSTER_PLAETZE = 4;

/**
 * Agenda-Linie (Variante „Linie“, Besprechung-Phasen, Betreiber 19.10.2026 „T2“): statt des Nummernkästchens ein runder Kreis, durch eine senkrechte Linie mit den
 * Nachbarn verbunden. Nur dort, wo die Linie etwas sagt (Fortschritt der Besprechung: Reiter „Durchführen“); Vorbereiten und Nachbereiten behalten das Kästchen.
 * Stand des Punkts: erledigt = Haken, aktuell (`aktuell`) = Ring, vertagt = gestrichelt mit Kalender, offen = leer mit Nummer. `oben`/`unten`: die Linie zum
 * Vorgänger bzw. Nachfolger (durchgezogen bis zum aktuellen Punkt, danach gestrichelt; `keine` am Anfang bzw. Ende). Der Aufrufer rechnet sie aus der Liste
 * (`linienVerlauf` in der Fachschicht), der Baustein zeichnet nur.
 */
export type PunktStandLinie = "offen" | "erledigt" | "vertagt";
export type PunktLinienArt = "keine" | "durch" | "gestrichelt";
export interface PunktLinie {
  stand: PunktStandLinie;
  oben: PunktLinienArt;
  unten: PunktLinienArt;
}
/** Zustand als Text für Hilfstechnik (der Zustand steht nie nur als Form oder Farbe). */
export const PUNKT_STAND_SR: Record<PunktStandLinie, string> = { offen: "offen", erledigt: "erledigt", vertagt: "vertagt" };

/** Feste, vollständige Klassenketten je Zustand des Kreises (nie zusammengesetzt); der laufende Punkt ist ein eigener Schlüssel (`hervorgehoben`). */
const KREIS_LAUFEND = "laufend";
const STAND_VORGABE: PunktStandLinie = "offen";
type KreisSchluessel = PunktStandLinie | typeof KREIS_LAUFEND;
const LINIE_KREIS_KLASSE: Record<KreisSchluessel, string> = {
  erledigt: "inline-flex size-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-medium tabular-nums bg-primary border-primary text-primary-foreground",
  laufend: "inline-flex size-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-medium tabular-nums bg-layer border-primary text-primary ring-4 ring-primary-200",
  vertagt: "inline-flex size-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-medium tabular-nums bg-layer border-dashed border-line-3 text-muted-foreground-1",
  offen: "inline-flex size-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-medium tabular-nums bg-layer border-line-3 text-muted-foreground-1",
};
/** Feste, vollständige Klassenketten für das Linienstück vor (`h-3`: bis zum Kreis) und nach (`grow`: bis zum Ende der Zeile) dem Kreis; 2 px, mittig in der Spalte. */
const LINIE_OBEN: Record<PunktLinienArt, string> = {
  keine: "h-3",
  durch: "h-3 border-s-2 border-primary",
  gestrichelt: "h-3 border-s-2 border-dashed border-line-3",
};
const LINIE_UNTEN: Record<PunktLinienArt, string> = {
  keine: "grow",
  durch: "grow border-s-2 border-primary",
  gestrichelt: "grow border-s-2 border-dashed border-line-3",
};

export interface PunktKennzeichen {
  id: string;
  /** Symbol (Lucide, vom Aufrufer, `aria-hidden`). */
  symbol: ReactNode;
  /** Zahl; bei 0 (oder weniger) zeigt der Baustein das Kennzeichen nicht. */
  zahl: number;
  /** Zugänglicher Satz („2 finden das wichtig“, „1 Bezug“, „3 Kommentare“). */
  label: string;
}

export interface PunktMenueEintrag {
  id: string;
  label: string;
  symbol?: ReactNode;
  gefahr?: boolean;
  disabled?: boolean;
  /** Der Eintrag öffnet einen Dialog oder ein Feld. */
  haspopup?: "dialog";
  beiWahl: () => void;
}

/**
 * Zeilenmenü „⋮“ der Punkt-Zeile: nur die Einträge, die der Aufrufer anbietet (Anbieten ≤ Erlauben); ohne Eintrag entfällt das Menü ganz.
 * Das Menü ist nie die Sperre, jede Action prüft selbst.
 */
export function PunktMenue({ label, eintraege }: { label: string; eintraege: readonly PunktMenueEintrag[] }) {
  if (eintraege.length === 0) return null;
  return (
    <>
      {/* Preline: component/dropdown/dropdown-dividers (über DropdownMenue; Zeilenmenü wie das der Aktionspunkte) */}
      <DropdownMenue
        ausloeserKlasse={PUNKT_MENUE_KNOPF}
        ausloeserLabel={label}
        ausloeser={
          <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="1" />
            <circle cx="12" cy="5" r="1" />
            <circle cx="12" cy="19" r="1" />
          </svg>
        }
      >
        {({ schliessen }) => (
          <div className="w-56 p-1 space-y-0.5">
            {eintraege.map((e) => (
              <button
                key={e.id}
                type="button"
                role="menuitem"
                className={e.gefahr ? MENUE_EINTRAG_GEFAHR : MENUE_EINTRAG}
                disabled={e.disabled}
                aria-haspopup={e.haspopup}
                onClick={() => {
                  schliessen();
                  e.beiWahl();
                }}
              >
                {e.symbol}
                {e.label}
              </button>
            ))}
          </div>
        )}
      </DropdownMenue>
    </>
  );
}

export function PunktZeile({
  id,
  nummer,
  titel,
  kennzeichen = [],
  vortragende = null,
  marke,
  aktuell = false,
  aktuellText = "Aktueller Punkt",
  linie,
  menue,
  offen: offenVorgabe,
  offenStart = false,
  beimUmschalten,
  testId,
  children,
}: {
  /** Kennung für Tests (`data-testid` am Listeneintrag). */
  testId?: string;
  /** Stabile Kennung der Zeile (Ziel für den Fokus nach Aktionen: `<id>-kopf` ist der Knopf der Zeile). */
  id?: string;
  nummer: number | string;
  titel: string;
  /** Kennzeichen in fester Reihenfolge (wichtig, Bezüge, Kommentare); nur mit `zahl > 0` sichtbar. */
  kennzeichen?: readonly PunktKennzeichen[];
  /** Vortragende Person (Initialen im Kreis); `label` ist der zugängliche Satz („Trägt vor: Mira Frostwind“). */
  vortragende?: { name: string; label: string } | null;
  /** Marke hinter dem Titel (Badge, z. B. „Erledigt“, „Kein Ergebnis“); vom Aufrufer. */
  marke?: ReactNode;
  /** Der Punkt läuft gerade (Marke „Aktueller Punkt“, `aria-current="step"`). */
  aktuell?: boolean;
  aktuellText?: string;
  /**
   * Variante „Linie“ (siehe `PunktLinie`): runder Kreis mit Linie statt des Nummernkästchens. Der laufende Punkt (`aktuell`) zeigt dann den Ring und KEINE Pille: der Ring
   * ersetzt die Marke (der Titel behält Platz, der Zustand steht für Hilfstechnik im Namen der Zeile, `aktuellText`); andere Marken (`marke`) bleiben Sache des Aufrufers.
   */
  linie?: PunktLinie;
  /** Zeilenmenü (`PunktMenue`) oder nichts; steht außerhalb des Aufklapp-Knopfs. */
  menue?: ReactNode;
  /** Gesteuert: der Aufrufer hält den Zustand (dann gilt `beimUmschalten`). */
  offen?: boolean;
  offenStart?: boolean;
  beimUmschalten?: (offen: boolean) => void;
  /** Aufklappbereich; wird erst beim Aufklappen gerendert. */
  children?: ReactNode;
}) {
  const [offenIntern, setOffen] = useState(offenStart);
  const offen = offenVorgabe ?? offenIntern;
  const eigeneId = useId();
  const basis = id ?? eigeneId;
  const umschalten = () => {
    const neu = !offen;
    setOffen(neu);
    beimUmschalten?.(neu);
  };
  // Von außen geöffnet (gesteuert): der Zustand folgt, damit ein späterer Wechsel auf ungesteuert nichts verliert.
  useEffect(() => {
    if (offenVorgabe !== undefined) setOffen(offenVorgabe);
  }, [offenVorgabe]);

  const vorhanden = kennzeichen.filter((k) => k.zahl > 0);
  const plaetze = vorhanden.length + (vortragende ? 1 : 0);
  const zeigeVon = plaetze > PUNKT_CLUSTER_PLAETZE ? PUNKT_CLUSTER_PLAETZE - 1 : PUNKT_CLUSTER_PLAETZE;
  const sichtbar = vorhanden.slice(0, zeigeVon - (vortragende ? 1 : 0));
  const verdeckt = plaetze - (sichtbar.length + (vortragende ? 1 : 0));
  const pfeil = (
    <svg className={offen ? "shrink-0 size-4 text-muted-foreground-1 rotate-180" : "shrink-0 size-4 text-muted-foreground-1"} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );

  const kreisSchluessel: KreisSchluessel = aktuell ? KREIS_LAUFEND : (linie?.stand ?? STAND_VORGABE);
  const zeile = (
    <>
        <div className="min-h-14 flex items-center gap-x-1">
          <button type="button" id={`${basis}-kopf`} className={KOPF_KNOPF} aria-expanded={offen} aria-controls={`${basis}-inhalt`} onClick={umschalten}>
            {!linie && (
              <span className={NUMMER} aria-hidden="true">
                {nummer}
              </span>
            )}
            <span className="sr-only">{`Punkt ${nummer}: `}</span>
            <span className="grow min-w-0 truncate text-base text-foreground">{titel}</span>
            {/* Der Zustand steht nie nur als Form: Text für Hilfstechnik hinter dem Titel */}
            {linie && <span className="sr-only">{`, ${aktuell ? aktuellText : PUNKT_STAND_SR[linie.stand]}`}</span>}
            {aktuell && !linie && (
              <span className="shrink-0">
                <Badge ton="primaer" punkt>
                  {aktuellText}
                </Badge>
              </span>
            )}
            {marke && <span className="shrink-0">{marke}</span>}
            {plaetze > 0 && (
              <span className="shrink-0 flex items-center gap-x-3 text-xs text-muted-foreground-1" data-testid="punkt-kennzeichen">
                {sichtbar.map((k) => (
                  <span key={k.id} className="inline-flex items-center gap-x-1" data-kennzeichen={k.id}>
                    {k.symbol}
                    <span aria-hidden="true" className="tabular-nums">
                      {k.zahl}
                    </span>
                    <span className="sr-only">{k.label}</span>
                  </span>
                ))}
                {verdeckt > 0 && (
                  <span className="tabular-nums" data-kennzeichen="mehr">
                    {`+${verdeckt}`}
                  </span>
                )}
                {vortragende && (
                  <span className={KREIS} data-kennzeichen="vortrag" title={vortragende.label}>
                    <span aria-hidden="true">{initialen(vortragende.name)}</span>
                    <span className="sr-only">{vortragende.label}</span>
                  </span>
                )}
              </span>
            )}
          </button>
          {menue}
          {/* Der Pfeil bedient dieselbe Zeile mit der Maus; die Tastatur und die Hilfstechnik nutzen den Knopf der Zeile. */}
          <button type="button" tabIndex={-1} aria-hidden="true" className="shrink-0 size-8 inline-flex items-center justify-center rounded-md focus:outline-hidden" onClick={umschalten}>
            {pfeil}
          </button>
        </div>
        <div id={`${basis}-inhalt`} role="region" aria-labelledby={`${basis}-kopf`} hidden={!offen} className={linie ? "pe-2 pb-4" : "ps-11 pe-2 pb-4"}>
          {offen && children}
        </div>
    </>
  );

  return (
    <>
      {/* Preline: data-display/data-display-sub-operational-activity-views/data-display-cat-list-groups/icon-action-list-with-dashed-dividers */}
      {/* Abweichung AW-121: Zeile mit Kennzeichen-Cluster, ⋮-Menü und Aufklappbereich (Aufklappen nach component/accordion/accordion-always-open) */}
      {linie ? (
        <>
          {/* Preline: data-display/data-display-sub-operational-activity-views/data-display-cat-timelines/order-status-timeline */}
          {/* Variante „Linie“ (Geometrie: Kreis und senkrechte Linie), Abweichung AW-121 (8) */}
          <li className="flex gap-x-3" aria-current={aktuell ? "step" : undefined} data-aktuell={aktuell ? "ja" : undefined} data-stand={linie.stand} data-testid={testId}>
            <div className="shrink-0 w-8 flex flex-col items-center" aria-hidden="true" data-linienspalte="ja">
              <span className={LINIE_OBEN[linie.oben]} data-linie-oben={linie.oben} />
              <span className={LINIE_KREIS_KLASSE[kreisSchluessel]} data-kreis={aktuell ? "aktuell" : linie.stand}>
                {aktuell || linie.stand === "offen" ? (
                  nummer
                ) : linie.stand === "erledigt" ? (
                  <svg className="size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                ) : (
                  <svg className="size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 2v4" />
                    <path d="M16 2v4" />
                    <rect width="18" height="18" x="3" y="4" rx="2" />
                    <path d="M3 10h18" />
                  </svg>
                )}
              </span>
              <span className={LINIE_UNTEN[linie.unten]} data-linie-unten={linie.unten} />
            </div>
            <div className="grow min-w-0">{zeile}</div>
          </li>
        </>
      ) : (
        <li className="border-t border-dashed border-line-2 first:border-t-0" aria-current={aktuell ? "step" : undefined} data-aktuell={aktuell ? "ja" : undefined} data-testid={testId}>
          {zeile}
        </li>
      )}
    </>
  );
}
